import "~/modules/shared/chat/chat-panel/ChatPanel.css";
import { createEffect } from "solid-js";
import type { ChatPanelController } from "~/modules/shared/chat/chat-panel/ChatPanel";
import type { ChatPanelUiState } from "~/modules/shared/chat/chat-panel/chatPanelState";
import { ChatTranscript } from "~/modules/shared/chat/chat-panel/components/ChatTranscript";
import { useChatPanelController } from "~/modules/shared/chat/chat-panel/useChatPanelController";

type Props = {
	handle: string;
	questionId: string;
	/** Ratings are owner-scoped, so only a viewer linked to the question can load them. */
	loadRatings: boolean;
	/** The creator's own social account public ids; their avatars are kept out of the thinking ticker. */
	creatorSocialAccountIds?: readonly string[];
	onSettled: (error?: string) => void;
	/**
	 * Called once the answer is complete — the same moment the authenticated
	 * chat enables its reply box. Never called if the answer fails to load.
	 */
	onAnswered?: () => void;
	/** Receives the conversation's live state, once, as it starts loading. */
	onState?: (state: ChatPanelUiState) => void;
	/**
	 * Receives the conversation's controller, once, before it starts loading,
	 * so whatever is set on it applies from the first streamed event.
	 */
	onController?: (controller: ChatPanelController) => void;
	ref?: (el: HTMLElement) => void;
};

/**
 * One question of a public AskPage thread, rendered by the shared chat
 * transcript. Loaded from the public stream endpoint, which relays the same
 * events as the authenticated question endpoint.
 */
export const AskPageConversation = (props: Props) => {
	let observersInitialized = false;

	const { state, controller, setupQueryContextObservers } =
		useChatPanelController({
			questionId: props.questionId,
			projectId: null,
			completionType: "influencer",
			conversationUrl: `/api/public/ask-page/${props.handle}/questions/${props.questionId}/stream`,
			loadRatings: props.loadRatings,
			onConversationSettled: (error) => props.onSettled(error),
			excludedTickerSocialAccountIds: props.creatorSocialAccountIds,
		});

	props.onState?.(state);
	props.onController?.(controller);

	createEffect(() => {
		if (!state.replyDisabled) {
			props.onAnswered?.();
		}
	});

	return (
		<section class="conversation" ref={(el) => props.ref?.(el)}>
			<ChatTranscript
				state={state}
				questionId={props.questionId}
				projectId={null}
				controller={controller}
				onTranscriptRef={(el) => {
					if (!observersInitialized) {
						observersInitialized = true;
						setupQueryContextObservers(el);
					}
				}}
			/>
		</section>
	);
};
